import * as React from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

/**
 * A single-choice filter for a `Table`'s bar: the property's name, the chosen
 * value once there is one, and a popover holding the choices.
 *
 * The one-value sibling of `FilterMenu`, for a filter the API takes one of — a
 * venue, a role, a status — and drawn the same way, so the two read as one
 * family: outlined while unset, `accent-soft` once set, with an × that clears
 * it. The events and users lists each had their own copy of this, and the two
 * had drifted apart in everything but name.
 */
export function FilterChip({
  property,
  valueLabel,
  icon: Icon,
  children,
  onClear,
}: {
  property: string
  /** The chosen value's label; null or empty while the filter is unset. */
  valueLabel?: string | null
  icon?: React.ComponentType<{ className?: string }>
  /** The popover's contents — usually `FilterOption`s — given a way to close it. */
  children: (close: () => void) => React.ReactNode
  onClear?: () => void
}): React.ReactElement {
  const [open, setOpen] = React.useState(false)
  const active = valueLabel != null && valueLabel !== ''
  const close = (): void => setOpen(false)

  return (
    // modal=false so nested date pickers can open without fighting focus trap.
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <div
        className={cn(
          'inline-flex h-[30px] max-w-full items-center rounded-md border text-control font-medium transition-colors',
          active
            ? 'border-transparent bg-accent-soft text-accent-ink'
            : 'border-line-control bg-surface text-ink-muted shadow-xs hover:bg-surface-hover hover:text-ink',
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex h-full min-w-0 items-center gap-1.5 rounded-md px-2.5 outline-none focus-visible:focus-ds"
          >
            {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" /> : null}
            <span className="shrink-0">{property}</span>
            {active ? (
              <>
                <span className="shrink-0 font-normal opacity-80">is</span>
                <span className="min-w-0 truncate">{valueLabel}</span>
              </>
            ) : null}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        {active && onClear ? (
          <button
            type="button"
            aria-label={`Clear ${property} filter`}
            onClick={onClear}
            className="grid h-full shrink-0 place-items-center rounded-r-md px-1.5 outline-none transition-colors hover:text-ink focus-visible:focus-ds"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      <PopoverContent align="start" className="w-64 p-1">
        {children(close)}
      </PopoverContent>
    </Popover>
  )
}

/** One choice in a `FilterChip`'s popover, checked when it is the current one. */
export function FilterOption({
  selected,
  onSelect,
  children,
}: {
  selected: boolean
  onSelect: () => void
  children: React.ReactNode
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-sm outline-none transition-colors',
        selected ? 'bg-accent-soft font-medium text-accent-ink' : 'text-ink hover:bg-surface-hover',
      )}
    >
      <span className="grid h-4 w-4 shrink-0 place-items-center">
        {selected ? <Check className="h-3.5 w-3.5" /> : null}
      </span>
      <span className="min-w-0 truncate">{children}</span>
    </button>
  )
}

/** The bar's "Clear" beside a list's filters, shown only while one is set. */
export function FilterClear({ onClear, children = 'Clear' }: { onClear: () => void; children?: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClear}
      className="h-[30px] rounded-md px-2 text-control font-medium text-ink-muted outline-none transition-colors hover:bg-surface-hover hover:text-ink focus-visible:focus-ds"
    >
      {children}
    </button>
  )
}
